import { useCallback, useSyncExternalStore } from "react";

/**
 * 任意のメディアクエリを購読する汎用フック。
 *
 * `useResponsiveLayout.ts`（useIsTwoColumn）と同じ
 * `useSyncExternalStore` + 共有 MediaQueryList 方式の汎用版。
 * 同じクエリ文字列を購読するコンポーネント間で MediaQueryList を 1 つだけ共有する。
 *
 * ページ全体の 1 / 2 カラム分岐には専用の `useIsTwoColumn` を使い、
 * それ以外の幅分岐（モーダル内レイアウト等）にはこちらを使うこと。
 */

// クエリ文字列ごとに MediaQueryList をモジュールで共有する
const mediaQueryLists = new Map<string, MediaQueryList>();

const getMediaQueryList = (query: string): MediaQueryList | null => {
  if (typeof window === "undefined") return null;
  let mql = mediaQueryLists.get(query);
  if (!mql) {
    mql = window.matchMedia(query);
    mediaQueryLists.set(query, mql);
  }
  return mql;
};

export const useMediaQuery = (query: string): boolean => {
  const subscribe = useCallback(
    (onStoreChange: () => void): (() => void) => {
      const mql = getMediaQueryList(query);
      if (!mql) return () => {};
      mql.addEventListener("change", onStoreChange);
      return () => mql.removeEventListener("change", onStoreChange);
    },
    [query],
  );

  const getSnapshot = useCallback(
    (): boolean => getMediaQueryList(query)?.matches ?? false,
    [query],
  );

  return useSyncExternalStore(subscribe, getSnapshot);
};

export default useMediaQuery;
